/**
 * Klausurtermin-Feld: wahlweise ein Datum eintragen (Standard – schnell
 * getippt) oder eine der Stunden des Fachs aus dem Stundenplan wählen, bis
 * zu den nächsten Sommerferien, nach Monat gruppiert. Ein Knopf im Feld
 * schaltet zwischen beiden um. Passt ein eingetragenes Datum zu einer
 * Stunde des Fachs, wird es automatisch mit ihr verknüpft (periodFrom/To) –
 * dann erscheint der Termin beim nächsten Öffnen direkt im Stunden-Modus.
 */

import { h, clear } from "@/lib/dom";
import { button, textInput } from "@/components/ui";
import { timetableService, holidaysService, findHoliday, nextSummerBreakEnd } from "@/services";
import {
  WEEKDAY_LABEL,
  addDays,
  buildBlocks,
  formatShortDate,
  toLocalISODate,
  upcomingSubjectSlots,
  weekdayOf,
  type UpcomingSlot,
} from "@/features/timetable/schedule";
import type { Period, TimetableSlot } from "@/types/models";

export interface ExamTermValue {
  date: string; // YYYY-MM-DD
  periodFrom?: number;
  periodTo?: number;
}

type Mode = "slot" | "date";

export function examTermField(opts: {
  subjectId: string;
  value?: ExamTermValue;
  /** Frühester Termin (Standard: heute). Hausaufgaben starten bei morgen. */
  from?: Date;
  /** Startmodus, wenn kein Wert vorgegeben ist (Standard: „slot"). */
  defaultMode?: Mode;
  /** Wenn true: ersten verfügbaren Slot vorwählen statt „Noch kein Termin". Hausaufgaben nutzen das. */
  autoSelect?: boolean;
  onChange?: () => void;
}): {
  el: HTMLElement;
  getValue: () => ExamTermValue;
  setSubject: (subjectId: string) => void;
  setMode: (m: Mode) => void;
} {
  let subjectId = opts.subjectId;
  // Termine liegen im Regelfall auf einer Stunde des Fachs – nur ein
  // bestehender Termin ohne Stundenplan-Bezug startet im Datums-Modus.
  let mode: Mode = opts.value
    ? opts.value.periodFrom
      ? "slot"
      : "date"
    : (opts.defaultMode ?? "slot");
  let slots: UpcomingSlot[] = [];
  let rawSlots: TimetableSlot[] = [];
  let periods: Period[] = [];
  // -1 = "noch kein Termin" (siehe buildSlotSelect). Startet dort statt bei
  // 0: sonst war die Dropdown-Auswahl nie wirklich leer, sondern zeigte
  // ungefragt die nächste Stunde als "gewählten" Termin – ein Thema bekam so
  // schon durch bloßes Öffnen+Speichern der Einstellungen einen Prüfungstermin
  // und sprang, sobald der vorbei war, unkontrolliert auf "erledigt".
  let selected = -1;
  let customDate = opts.value?.date ?? "";
  let dateInput: HTMLInputElement | null = null;
  let loaded = false;

  const body = h("div", { class: "term-field" });
  const el = h("div", null, body);

  function slotLabel(s: UpcomingSlot): string {
    const start = periods[s.from - 1]?.start ?? "";
    const end = periods[s.to - 1]?.end ?? "";
    const stunde = s.from === s.to ? `${s.from}. Stunde` : `${s.from}.–${s.to}. Stunde`;
    return `${WEEKDAY_LABEL[s.weekday]}, ${formatShortDate(s.date)} · ${stunde} (${start}–${end})`;
  }

  /** Passt das getippte Datum zu einer Stunde des Fachs? Dann Periode automatisch mitverknüpfen. */
  function deriveSlotForDate(dateIso: string): { periodFrom: number; periodTo: number } | undefined {
    if (!dateIso || !periods.length) return undefined;
    const weekday = weekdayOf(new Date(`${dateIso}T00:00:00`));
    const block = buildBlocks(rawSlots, weekday, periods.length).find((b) => b.subject === subjectId);
    return block ? { periodFrom: block.from, periodTo: block.from + block.span - 1 } : undefined;
  }

  // Ohne Stunden im Stundenplan früher automatisch auf den Datums-Modus
  // gefallen – der zeigt ein natives <input type="date">, und auf dem Handy
  // (v.a. iOS) genügt teils schon ein Antippen, damit es ungefragt das
  // heutige Datum einträgt statt leer zu bleiben, obwohl "kein Termin"
  // gemeint war. Jetzt bleibt es beim Slot-Modus, auch ohne Stunden – die
  // Auswahl zeigt dann nur "Noch kein Termin" und sonst nichts, statt in ein
  // fehleranfälliges freies Datumsfeld zu wechseln.
  function effectiveMode(): Mode {
    return mode === "slot" ? "slot" : "date";
  }

  /** Gruppierter <select> (optgroup je Kalendermonat) – „Single Choice" über den ganzen Zeitraum. */
  function buildSlotSelect(): HTMLSelectElement {
    const sel = h("select", {
      class: "select",
      onchange: (e: Event) => {
        selected = Number((e.target as HTMLSelectElement).value);
        opts.onChange?.();
      },
    }) as HTMLSelectElement;

    sel.appendChild(h("option", { value: "-1", selected: selected === -1 }, "Noch kein Termin"));

    let currentGroup: HTMLOptGroupElement | null = null;
    let currentKey = "";
    slots.forEach((s, i) => {
      const key = `${s.date.getFullYear()}-${s.date.getMonth()}`;
      if (key !== currentKey) {
        const label = s.date.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
        currentGroup = h("optgroup", { label: label.charAt(0).toUpperCase() + label.slice(1) });
        sel.appendChild(currentGroup);
        currentKey = key;
      }
      currentGroup!.appendChild(h("option", { value: String(i), selected: i === selected }, slotLabel(s)));
    });
    return sel;
  }

  function render(): void {
    clear(body);
    if (!loaded) {
      body.appendChild(h("div", { class: "input term-field-main" }, "Lädt Stundenplan …"));
      body.appendChild(button({ icon: "calendar", variant: "ghost", disabled: true, title: "Lädt …" }));
      return;
    }
    const active = effectiveMode();

    if (active === "slot") {
      body.appendChild(h("div", { class: "term-field-main" }, buildSlotSelect()));
    } else {
      const input = textInput({ type: "date", value: customDate });
      dateInput = input;
      // Manche Browser feuern beim Leeren über das native "×" im
      // Datums-Picker kein zuverlässiges `input`-Ereignis, nur `change` –
      // ohne beide bliebe `customDate` auf dem alten Wert hängen, während das
      // Feld schon leer aussieht (beim Speichern springt dann der alte/
      // heutige Tag wieder rein).
      const sync = () => {
        customDate = input.value;
        opts.onChange?.();
      };
      input.addEventListener("input", sync);
      input.addEventListener("change", sync);
      body.appendChild(h("div", { class: "term-field-main" }, input));
    }

    body.appendChild(
      button({
        icon: active === "slot" ? "calendar" : "clock",
        variant: "ghost",
        // Der Umschalter geht jetzt IMMER in beide Richtungen: der Slot-Modus
        // zeigt auch ohne Stunden im Stundenplan wenigstens "Noch kein
        // Termin" an (kein Sackgassen-Zustand mehr ohne Weg zurück).
        title:
          active === "slot"
            ? "Stattdessen ein eigenes Datum eintragen"
            : "Stattdessen eine Stunde aus dem Stundenplan wählen",
        onClick: () => {
          mode = active === "slot" ? "date" : "slot";
          render();
          opts.onChange?.();
        },
      })
    );
  }

  async function load(): Promise<void> {
    const [settings, allSlots] = await Promise.all([
      timetableService.getSettings(),
      timetableService.listSlots(),
    ]);
    periods = settings.periods;
    rawSlots = allSlots;

    const today = opts.from ?? new Date();
    // Grosszuegig vorausladen (gecached je Kalenderjahr), um daraus das Ende
    // der naechsten Sommerferien zu bestimmen.
    const holidays = await holidaysService.forRange(today, addDays(today, 400));
    const boundary = nextSummerBreakEnd(holidays, today) ?? addDays(today, 84);
    const weeksAhead = Math.max(1, Math.ceil((boundary.getTime() - today.getTime()) / (7 * 86_400_000)));

    const candidates = upcomingSubjectSlots({
      slots: allSlots,
      periods,
      days: settings.days,
      subjectId,
      from: today,
      weeksAhead,
    });
    // Alle Stunden bis zu den Sommerferien, nur Ferien-/Feiertage raus.
    slots = candidates.filter(
      (s) => s.date.getTime() <= boundary.getTime() && !findHoliday(holidays, toLocalISODate(s.date))
    );

    // Bestehenden Wert (falls vorhanden) im Dropdown wiederfinden.
    if (opts.value?.periodFrom) {
      const idx = slots.findIndex(
        (s) =>
          toLocalISODate(s.date) === opts.value?.date &&
          s.from === opts.value?.periodFrom &&
          s.to === opts.value?.periodTo
      );
      if (idx >= 0) selected = idx;
    } else if (opts.autoSelect && slots.length && selected === -1) {
      // Hausaufgaben: erste bevorstehende Stunde vorwählen statt leer zu lassen.
      selected = 0;
    }
    loaded = true;
    render();
  }
  void load();
  render();

  return {
    el,
    getValue(): ExamTermValue {
      if (effectiveMode() === "slot") {
        // -1 ("Noch kein Termin") MUSS hier sofort leer zurückgeben. Sonst
        // fiel es weiter unten auf `customDate` zurück – und die trug bei
        // einem BESTEHENDEN Termin noch den alten Wert von `opts.value`, den
        // das bloße Umschalten der Auswahl nie anfasst. Ergebnis: "Noch kein
        // Termin" wählen und speichern schrieb heimlich den alten Termin
        // zurück, der irgendwann verging und das Thema "erledigt" markierte.
        if (selected === -1) return { date: "", periodFrom: undefined, periodTo: undefined };
        const s = slots[selected];
        if (s) return { date: toLocalISODate(s.date), periodFrom: s.from, periodTo: s.to };
      }
      // Direkt aus dem Feld lesen statt nur aus `customDate`: manche Browser
      // feuern beim Leeren über das native "×" kein verlässliches Ereignis
      // (siehe render()) – so zählt immer der tatsächlich sichtbare Stand.
      const live = dateInput ? dateInput.value : customDate;
      if (!live) return { date: "", periodFrom: undefined, periodTo: undefined };
      const derived = deriveSlotForDate(live);
      return { date: live, periodFrom: derived?.periodFrom, periodTo: derived?.periodTo };
    },
    setSubject(id: string): void {
      subjectId = id;
      selected = 0;
      loaded = false;
      render();
      void load();
    },
    setMode(m: Mode): void {
      mode = m;
      render();
    },
  };
}
